import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from './redux';
import { ServerMessagingProvider } from '../services/messaging/providers/ServerMessagingProvider';
import { IMessage } from '../services/messaging/IMessagingProvider';
import { NotificationService } from '../utils/notifications';
import {
  setConnected,
  setConversations,
  setMessages,
  addMessage,
  setUserOnline,
  setUserOffline,
  markConversationAsRead
} from '../store/reducers/messages.reducer';

export const useMessaging = () => {
  const dispatch = useAppDispatch();
  const { loginUser } = useAppSelector((state) => state.user);
  const { conversations, currentConversation, messages, isConnected } = useAppSelector((state) => state.messages);
  const [provider, setProvider] = useState<ServerMessagingProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const providerRef = useRef<ServerMessagingProvider | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const lastConnectionAttemptRef = useRef(0);

  // Initialize messaging provider with reconnection throttling
  useEffect(() => {
    if (!loginUser?.id) return;

    // Don't reinitialize if provider already exists
    if (providerRef.current) {
      console.log('✅ Provider already initialized, skipping');
      return;
    }

    const initProvider = async () => {
      const now = Date.now();
      const timeSinceLastAttempt = now - lastConnectionAttemptRef.current;
      const minDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000); // Exponential backoff, max 30s

      // Throttle reconnection attempts
      if (timeSinceLastAttempt < minDelay) {
        console.log(`⏳ Reconnection throttled. Waiting ${Math.ceil((minDelay - timeSinceLastAttempt) / 1000)}s...`);
        setTimeout(initProvider, minDelay - timeSinceLastAttempt);
        return;
      }

      lastConnectionAttemptRef.current = now;
      reconnectAttemptsRef.current++;

      try {
        const senderName = loginUser.display_name || loginUser.email || 'Someone';
        const newProvider = new ServerMessagingProvider(String(loginUser.id), senderName);
        providerRef.current = newProvider;

        await newProvider.connect();
        setProvider(newProvider);
        dispatch(setConnected(true));

        // Reset reconnection counter on successful connection
        reconnectAttemptsRef.current = 0;
        console.log('✅ Messaging connected successfully');

        // Request notification permission
        await NotificationService.requestPermission();

        // Load conversations
        const convs = await newProvider.getAllConversations();
        console.log(`📋 Loaded ${convs.length} conversations`);
        dispatch(setConversations(convs));

        // Listen for new messages
        newProvider.onMessageReceived((message: IMessage) => {
          dispatch(addMessage({ ...message, currentUserId: String(loginUser.id) }));
        });

        // Listen for online status changes
        newProvider.onUserOnlineStatusChanged((userId: string, isOnline: boolean) => {
          if (isOnline) {
            dispatch(setUserOnline(userId));
          } else {
            dispatch(setUserOffline(userId));
          }
        });
      } catch (err: any) {
        console.error(`❌ Failed to initialize messaging (attempt ${reconnectAttemptsRef.current}):`, err);
        setError(err.message);
        dispatch(setConnected(false));

        // Clear provider ref on failure so we can retry
        providerRef.current = null;

        // Don't auto-retry if we've exceeded max attempts
        if (reconnectAttemptsRef.current >= 10) {
          console.error('🛑 Max reconnection attempts reached. Please refresh the page.');
          reconnectAttemptsRef.current = 0; // Reset for manual retry
        }
      }
    };

    initProvider();

    // Cleanup function - DO NOT disconnect on every unmount
    // Only disconnect when component is truly destroyed (user logs out)
    return () => {
      // Keep the provider alive for navigation/refresh
      // Only disconnect if user is logging out (loginUser becomes null)
    };
  }, [loginUser?.id, dispatch]);

  // Reload conversations when provider becomes available and conversations are empty
  useEffect(() => {
    if (!provider || !loginUser?.id) return;

    // If we have a provider but no conversations, reload them
    // This handles the case where Redux state was cleared on refresh
    if (conversations.length === 0 && isConnected) {
      console.log('📋 No conversations in state, reloading...');
      const loadConversations = async () => {
        try {
          const convs = await provider.getAllConversations();
          console.log(`📋 Reloaded ${convs.length} conversations after refresh`);
          dispatch(setConversations(convs));
        } catch (err) {
          console.error('Failed to reload conversations:', err);
        }
      };
      loadConversations();
    }
  }, [provider, conversations.length, isConnected, loginUser?.id, dispatch]);

  const sendMessage = useCallback(async (recipientId: string, content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; linkPreview?: any }) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');
    if (!loginUser?.id) throw new Error('User not logged in');

    try {
      const message = await providerRef.current.sendMessage(recipientId, content, mediaData);
      dispatch(addMessage({ ...message, currentUserId: String(loginUser.id) }));
      return message;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch, loginUser]);

  const loadMessages = useCallback(async (userId: string) => {
    if (!providerRef.current) return;

    try {
      const msgs = await providerRef.current.getMessages(userId);
      dispatch(setMessages({ userId, messages: msgs }));
    } catch (err: any) {
      setError(err.message);
    }
  }, [dispatch]);

  const markAsRead = useCallback(async (userId: string) => {
    if (!providerRef.current) return;

    const userMessages = messages[userId] || [];
    for (const msg of userMessages) {
      if (msg.recipientId === String(loginUser?.id) && msg.status !== 'read') {
        await providerRef.current.markAsRead(msg.id);
      }
    }

    dispatch(markConversationAsRead(userId));
  }, [messages, loginUser, dispatch]);

  const isUserOnline = useCallback(async (userId: string): Promise<boolean> => {
    if (!providerRef.current) return false;
    return await providerRef.current.isOnline(userId);
  }, []);

  const deleteConversation = useCallback(async (userId: string) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');

    try {
      await providerRef.current.deleteConversation(userId);
      // Reload conversations after deletion
      const convs = await providerRef.current.getAllConversations();
      dispatch(setConversations(convs));
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!providerRef.current) throw new Error('Messaging not initialized');

    try {
      await providerRef.current.deleteMessage(messageId);
      // Reload messages for current conversation
      if (currentConversation) {
        const msgs = await providerRef.current.getMessages(currentConversation);
        dispatch(setMessages({ userId: currentConversation, messages: msgs }));
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, [dispatch, currentConversation]);

  // Get current conversation messages - useMemo ensures this updates when messages change
  const currentMessages = useMemo(() => {
    if (!currentConversation) return [];
    return messages[currentConversation] || [];
  }, [currentConversation, messages]);

  return {
    isConnected,
    conversations,
    currentConversation,
    messages: currentMessages,
    error,
    sendMessage,
    loadMessages,
    markAsRead,
    isUserOnline,
    deleteConversation,
    deleteMessage
  };
};
